import * as _ from 'underscore';
import * as moment from 'moment';
import { SEM_AVG_POSITION } from '../../../reporting/analytics/constants/analytics.constants';
import { ColumnConfig } from '../../../reporting/analytics-core/interfaces/widget.interface';
/**
 *  Utility Service for misc. utilities
 *  Contains all static methods
 */
export class UtilityService {
	static isUndefinedOrNull(obj: any) {
		return typeof obj === 'undefined' || obj === null;
	}

	static isUndefined(obj: any) {
		return typeof obj === 'undefined';
	}

	static toTrueOrFalse(value) {
		if (typeof value !== 'boolean') {
			return !!parseInt(value);
		}
		return value;
	}

	static booleanToNumber(value: boolean) {
		return value ? 1 : 0;
	}

	static listToMatrix(list, elementsPerSubList) {
		const matrix = [];

		for (let i = 0, k = -1; i < list.length; i++) {
			if (i % elementsPerSubList === 0) {
				k++;
				matrix[k] = [];
			}

			matrix[k].push(list[i]);
		}

		return matrix;
	}

	static upperCaseFirst(string: string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	static calculateSumFromArray(array: number[]) {
		return _.reduce(
			array,
			(memo, num) => {
				return memo + num;
			},
			0
		);
	}

	static truncate(num, fixed) {
		fixed = fixed || 0;
		fixed = Math.pow(10, fixed);
		return Math.floor(num * fixed) / fixed;
	}

	/**
	 * Get all possible combinations from string array
	 *
	 * @param {any[]} array
	 * @returns {any[]}
	 */
	static getCombinations(array: string[]) {
		const result = [];
		const combination = function (prefix, chars) {
			for (let i = 0; i < chars.length; i++) {
				result.push(prefix + chars[i]);
				combination(prefix + chars[i], chars.slice(i + 1));
			}
		};
		combination('', array);
		return result;
	}

	/**
	 * Get all possible sub arrays from string array
	 *
	 * @param arr
	 * @returns {any}
	 */
	static getSubArrays(arr: string[]) {
		if (arr.length === 0) {
			return [];
		} else if (arr.length === 1) {
			return [arr];
		} else {
			const subarr = this.getSubArrays(arr.slice(1));
			return subarr.concat(
				subarr.map((e) => e.concat(arr[0])),
				[[arr[0]]]
			);
		}
	}

	/**
	 *
	 * Check's if a url is encoded
	 *
	 * @param str
	 */
	static checkEncodeURI(str): boolean {
		return /\%/i.test(str);
	}

	static checkForVisibilityPermissions(columns: ColumnConfig[], dateSet) {
		const { startDate, endDate, depricatedDate, goLiveDate } = dateSet;
		const isGoliveNotStarted = moment(endDate).isBefore(goLiveDate);
		const isCutoffDate = moment(startDate).isAfter(depricatedDate) || moment(startDate).isSame(depricatedDate);
		const avgPositionFilterText = SEM_AVG_POSITION.AVG_POSITION_KEYS;
		const newImpressionsFilterText = SEM_AVG_POSITION.IMPRESSIONS_KEYS;

		if (isGoliveNotStarted) {
			columns = columns.filter((item) => !newImpressionsFilterText.includes(item.columnKey));
		}
		if (isCutoffDate) {
			columns = columns.filter((item) => !avgPositionFilterText.includes(item.columnKey));
		}
		return columns;
	}

	static isNumber(o) {
		return !isNaN(o - 0) && o != null;
	}

	static isInfinite(number) {
		if (number === Infinity) {
			return true;
		} else {
			return false;
		}
	}

	static toJson(string) {
		return JSON.parse(string);
	}

	static stringify(string) {
		return JSON.stringify(string);
	}

	static toInt(string) {
		return parseInt(string);
	}

	static isNull(o) {
		return o === null;
	}

	static toFloat(string) {
		return parseFloat(string);
	}

	static toString(value) {
		return String(value);
	}

	static compareObjectsCaseInSensitive(obj1, obj2) {
		let status: any = true;
		status = _.find(_.keys(obj1), function (currentKey) {
			if (currentKey === 'Phone') {
				return false; // escape phone in address comparison
			}
			if (currentKey === 'included') {
				return false;
			}
			if (!(_.has(obj2, currentKey) && _.isEqual(obj1[currentKey].toLowerCase(), obj2[currentKey].toLowerCase()))) {
				return true;
			}
		});

		return status === undefined; // when all values are equal find loop returns undefined
	}
	/**
	 * @name getDates
	 * @desc process the required list of dates with required params within a range
	 * @params: startDate: any, endDate: any
	 * @memberOf UtilityService.getDates
	 */
	static getDates(startDate: any, endDate: any): any {
		const totalweeks = [];
		const crntWeek = [];
		let weekWise = [];
		let weeklyIndex = 0;
		const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
		const today = new Date();
		let currentDate = startDate;
		const addDays = function (days) {
			const date = new Date(this.valueOf());
			date.setDate(date.getDate() + days);
			return date;
		};
		while (currentDate <= endDate) {
			let isDisabled;
			isDisabled = currentDate.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0) ? 'disabled' : '';

			const day = currentDate.getDay();
			weekWise.push({
				date: currentDate.getDate(),
				day: dayNames[day],
				class: isDisabled,
				fullDate: moment(currentDate).format('MM/DD/YYYY')
			});
			if (weekWise.length % 7 === 0) {
				totalweeks.push(weekWise);
				if (today.setHours(0, 0, 0, 0) <= currentDate.setHours(0, 0, 0, 0)) {
					crntWeek.push(weekWise);
				} else if (today.setHours(0, 0, 0, 0) > endDate.setHours(0, 0, 0, 0)) {
					crntWeek.push(weekWise);
				}
				weekWise = [];
				weeklyIndex = weeklyIndex + 1;
			}
			currentDate = addDays.call(currentDate, 1);
		}
		return crntWeek;
	}

	/**
	 * @name getTimeDiff
	 * @desc get the time difference between 2 times
	 * @params: startTime: any, endTime: any
	 * @memberOf UtilityService.getTimeDiff
	 */
	static getTimeDiff(startTime: any, endTime: any) {
		const todayDate = moment(new Date()).format('MM/DD/YYYY');
		const startStr = todayDate + ' ' + startTime;
		const endStr = todayDate + ' ' + endTime;
		const todayStartTime: any = new Date(startStr);
		const todayEndTime: any = new Date(endStr);
		const minsVal: any = (todayEndTime - todayStartTime) / 60000;
		return minsVal;
	}

	/**
	 * @name getFormattedTime
	 * @desc get the time formatting with some default date
	 * @params: time: any
	 * @memberOf UtilityService.getFormattedTime
	 */
	static getFormattedTime(time: any) {
		const todayDate = moment(new Date()).format('MM/DD/YYYY');
		const startStr = todayDate + ' ' + time;
		const todayTime: any = new Date(startStr);
		return todayTime;
	}

	/**
	 * @name getActiveMondaysList
	 * @desc get the list of mondays from the current date
	 * @memberOf UtilityService.getActiveMondaysList
	 */
	static getActiveMondaysList() {
		const dateArr = [];
		const todayDate = new Date();
		const crntDay = todayDate.getDay();
		const crntDate = todayDate.getDate();
		const activeDays = crntDay === 1 ? 7 : 14;
		const moveToMon = crntDay === 1 ? crntDate : crntDate - (crntDay - 1);
		const nextActiveDate = todayDate.setDate(moveToMon + activeDays);
		dateArr.push({ date: new Date(nextActiveDate), formattedDate: moment(nextActiveDate).format('MM/DD/YYYY') });
		for (let i = 1; i < 24; i++) {
			const crntVisible = dateArr[i - 1].date;
			const crntVisibleDate = crntVisible.getDate();
			const nextActive = todayDate.setDate(crntVisibleDate + 7);
			dateArr.push({ date: new Date(nextActive), formattedDate: moment(nextActive).format('MM/DD/YYYY') });
		}
		return dateArr;
	}

	static isFloat(n: any): any {
		return Number(n) === n && n % 1 !== 0;
	}

	static isInteger(n: any): any {
		return Number(n) === n && n % 1 === 0;
	}
}
